import { useContext, useState } from 'react';
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from '@stripe/react-stripe-js';
import { showToast } from '../../Components/utils/ShowToast';
import Loader from '../../Components/utils/Loader';
import { formatCurrency } from '../../data/constants';
import { LoginContext } from '../../Contexts/LoginContext';

const PaymentOverlay = ({
    isOpen,
    onClose,
    showTotalOnly = false,
    paymentDetails,
    createPaymentIntent,
    onPaymentSuccess = (paymentIntent) => { },
    title = 'Payment'
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const {currency, exchangeRate} = useContext(LoginContext);

    if (!isOpen) return null;

    const handlePayment = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!stripe || !elements) {
            showToast('Stripe is not loaded', 'error');
            setLoading(false);
            return;
        }

        const cardElement = elements.getElement(CardNumberElement);
        if (!cardElement) {
            showToast('Please enter card details', 'error');
            setLoading(false);
            return;
        }

        try {
            const { error, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            });

            if (error) {
                showToast(error.message, 'error');
                setLoading(false);
                return;
            }

            // Call the provided createPaymentIntent function to get the client secret
            const clientSecret = await createPaymentIntent(paymentMethod.id);

            // Confirm the card payment with Stripe
            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
                payment_method: paymentMethod.id,
            });

            if (confirmError) {
                showToast(confirmError.message, 'error');
                setLoading(false);
                return;
            }

            if (paymentIntent.status === 'succeeded') {
                showToast('Payment successful', 'success');
                onPaymentSuccess(paymentIntent);
                onClose();
            }
        } catch (error) {
            console.log(error);
            showToast(error.message || 'An error occurred', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modalOverlay paymentModelOverlayDiv">
            <div className="modalContent">
                <div className="form">
                    <h2 className="primaryHeadingSmall"><span>{title}</span></h2>
                    <div className="horizontalLine"></div>
                    {!showTotalOnly && <> <div className='row'>
                        <span>Price</span>
                        <strong>{formatCurrency(paymentDetails.price, currency, exchangeRate)}</strong>
                    </div>
                        <div className='row'>
                            <span>Tax</span>
                            <strong>{formatCurrency(paymentDetails.tax, currency, exchangeRate)}</strong>
                        </div></>}
                    <div className='row'>
                        <span>Total</span>
                        <strong>{formatCurrency(paymentDetails.total, currency, exchangeRate)}</strong>
                    </div>
                    <form className='form'>
                        <div className="inputDiv">
                            <label>Card Number</label>
                            <CardNumberElement className="inputField" />
                        </div>
                        <div className="inputDiv">
                            <div className="inputInnerDiv">
                                <label>Expiry Date</label>
                                <CardExpiryElement className="inputField" />
                            </div>
                            <div className="inputInnerDiv">
                                <label>CVC</label>
                                <CardCvcElement className="inputField" />
                            </div>
                        </div>
                    </form>
                </div>
                <div className="buttonsDiv">
                    <button
                        className="modalButton cancel"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Close
                    </button>
                    <button
                        className="modalButton submit"
                        onClick={handlePayment}
                        disabled={loading}
                    >
                        {loading ? <Loader type="miniPlus" /> : `Pay ${formatCurrency(paymentDetails.total, currency, exchangeRate)}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentOverlay;